import React, { useState } from 'react';
import { XIcon, ArrowRightIcon, ArrowLeftIcon } from 'lucide-react';
interface InteractiveTourOverlayProps {
  isOpen: boolean;
  onClose: () => void;
}
export function InteractiveTourOverlay({
  isOpen,
  onClose
}: InteractiveTourOverlayProps) {
  const [currentStep, setCurrentStep] = useState(0);
  const steps = [{
    title: 'GTM Studio',
    description: 'Visual workspace for building and managing GTM programs',
    image: 'https://images.unsplash.com/photo-1551288049-bebda4e38f71?w=800&h=600&fit=crop',
    features: ['Drag-and-drop program builder', 'Account segmentation', 'Play templates library', 'Real-time collaboration']
  }, {
    title: 'Copilot',
    description: 'AI-powered guidance for every GTM motion',
    image: 'https://images.unsplash.com/photo-1677442136019-21780ecad995?w=800&h=600&fit=crop',
    features: ['Account summaries', 'Next best action', 'Buying group detection', 'AI email generation']
  }, {
    title: 'Dynamic Plays',
    description: 'Signal-based GTM actions you can run now',
    image: 'https://images.unsplash.com/photo-1460925895917-afdab827c52f?w=800&h=600&fit=crop',
    features: ['50+ pre-built plays', 'Custom play builder', 'Event-based triggers', 'Multi-channel orchestration']
  }];
  if (!isOpen) return null;
  const handleNext = () => {
    if (currentStep < steps.length - 1) {
      setCurrentStep(currentStep + 1);
    } else {
      onClose();
    }
  };
  const handlePrevious = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };
  return <>
      <div className="fixed inset-0 bg-black bg-opacity-50 z-50" onClick={onClose} data-id="element-3638" />
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4" data-id="element-3639">
        <div className="bg-white rounded-2xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-hidden" data-id="element-3640">
          {/* Header */}
          <div className="px-8 py-6 border-b border-gray-200 flex items-center justify-between" data-id="element-3641">
            <div data-id="element-3642">
              <h2 className="text-2xl font-bold text-gray-900" data-id="element-3643">
                Interactive Tour
              </h2>
              <p className="text-sm text-gray-600 mt-1" data-id="element-3644">
                Step {currentStep + 1} of {steps.length}
              </p>
            </div>
            <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-lg transition-colors" data-id="element-3645">
              <XIcon className="w-6 h-6 text-gray-500" data-id="element-3646" />
            </button>
          </div>
          {/* Content */}
          <div className="p-8" data-id="element-3647">
            <div className="grid grid-cols-2 gap-8" data-id="element-3648">
              {/* Left: Image */}
              <div data-id="element-3649">
                <img src={steps[currentStep].image} alt={steps[currentStep].title} className="w-full h-80 object-cover rounded-lg" data-id="element-3650" />
              </div>
              {/* Right: Content */}
              <div data-id="element-3651">
                <h3 className="text-3xl font-bold text-gray-900 mb-4" data-id="element-3652">
                  {steps[currentStep].title}
                </h3>
                <p className="text-gray-600 mb-6" data-id="element-3653">
                  {steps[currentStep].description}
                </p>
                <ul className="space-y-3" data-id="element-3654">
                  {steps[currentStep].features.map((feature, index) => <li key={index} className="flex items-center gap-3" data-id="element-3655">
                      <div className="w-6 h-6 bg-red-100 rounded-full flex items-center justify-center flex-shrink-0" data-id="element-3656">
                        <div className="w-2 h-2 bg-red-600 rounded-full" data-id="element-3657" />
                      </div>
                      <span className="text-gray-700" data-id="element-3658">{feature}</span>
                    </li>)}
                </ul>
              </div>
            </div>
          </div>
          {/* Footer */}
          <div className="px-8 py-6 border-t border-gray-200 flex items-center justify-between" data-id="element-3659">
            <button onClick={handlePrevious} disabled={currentStep === 0} className="flex items-center gap-2 px-4 py-2 text-gray-700 hover:text-gray-900 disabled:opacity-50 disabled:cursor-not-allowed" data-id="element-3660">
              <ArrowLeftIcon className="w-4 h-4" data-id="element-3661" />
              Previous
            </button>
            <div className="flex gap-2" data-id="element-3662">
              {steps.map((_, index) => <button key={index} onClick={() => setCurrentStep(index)} className={`w-2 h-2 rounded-full transition-colors ${index === currentStep ? 'bg-red-600' : 'bg-gray-300'}`} data-id="element-3663" />)}
            </div>
            <button onClick={handleNext} className="flex items-center gap-2 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700" data-id="element-3664">
              {currentStep === steps.length - 1 ? 'Finish' : 'Next'}
              <ArrowRightIcon className="w-4 h-4" data-id="element-3665" />
            </button>
          </div>
        </div>
      </div>
    </>;
}